import { Base } from '@studiometa/js-toolkit';
import { historyReplace, nextFrame } from '@studiometa/js-toolkit/utils';
import { Panel } from '@studiometa/ui';
import ListingLoadMore from './ListingLoadMore.js';
import Select from '../../atoms/Select/Select.js';
import Autocomplete from '../../atoms/Select/Autocomplete.js';
import InputFilterDate from '../../atoms/InputFilterDate/InputFilterDate.js';
import InputSearch from '../../atoms/InputSearch/InputSearch.js';
import InputText from '../../atoms/InputText.js';
import FilterTabs from '../../atoms/FilterTabs.js';
import Checkbox from '../../atoms/Checkbox.js';
import ListingTabs from './ListingTabs.js';

/**
 * @typedef {object} ListingRef
 * @property {HTMLElement} list
 * @property {HTMLButtonElement[]} reset
 * @property {HTMLFormElement} form
 * @property {HTMLElement} count
 * @property {HTMLElement} counter
 * @property {HTMLButtonElement} open
 * @property {HTMLButtonElement[]} close[]
 */
/**
 * @typedef {object} ListingInterface
 * @property {ListingRef} $refs
 */

/**
 * Listing component
 */
export default class Listing extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Listing',
    refs: [
      'list',
      'innerlist',
      'reset[]',
      'form',
      'count',
      'counter',
      'loadfromfilter[]',
      'openPanel',
      'close[]',
      'open',
    ],
    emits: ['show-loader', 'hide-loader'],
    components: {
      FilterTabs,
      ListingTabs,
      ListingLoadMore,
      InputText,
      Select,
      Autocomplete,
      InputFilterDate,
      InputSearch,
      Panel,
      Checkbox,
    },
    options: {
      endpoint: {
        type: String,
        default: '',
      },
      replace: {
        type: Boolean,
        default: false,
      },
      panelEndpoint: {
        type: String,
        default: '',
      },
      bundle: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * @member {string} panelTemplate
   */
  panelTemplate = '';

  /**
   * @returns {Array}
   */
  get formValues() {
    const formData = new FormData(this.$refs.form);
    return [...formData.entries()];
  }

  /**
   * Mounted hook
   */
  mounted() {
    window.addEventListener('popstate', this.onPopstate.bind(this));
  }

  /**
   * onLoadMoreClick
   * @this {Listing & ListingInterface}
   * @param {string} endpoint
   */
  onListingLoadMoreFetch(endpoint) {
    this.fetchData(endpoint, false, false, true);
  }

  /**
   * onListingTabsChanging
   * @this {Listing & ListingInterface}
   */
  onListingTabsChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onSelectChange
   * @this {Listing & ListingInterface}
   */
  onSelectChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onCheckboxChanging
   * @this {Listing & ListingInterface}
   */
  onCheckboxChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onAutocompleteChanging
   * @this {Listing & ListingInterface}
   */
  onAutocompleteChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onSelectChange
   * @this {Listing & ListingInterface}
   */
  onInputTextChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onInputDateChange
   * @this {Listing & ListingInterface}
   */
  onInputFilterDateChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * onInputDateChange
   * @this {Listing & ListingInterface}
   */
  onInputSearchChanging() {
    this.fetchData(`${this.$options.endpoint}`, true, true);
  }

  /**
   * fetchData
   * @this {Listing & ListingInterface}
   * @param {string} endpoint
   * @param {boolean} replace
   * @param {boolean} useFormValues
   * @param {boolean} innerList
   * @returns {Promise}
   */
  async fetchData(endpoint, replace = false, useFormValues = true, innerList = false) {
    const [clearEndpoint, rawEndpointParams = ''] = endpoint.split('?');
    const params = new URLSearchParams(rawEndpointParams);
    console.log(rawEndpointParams);
    console.log('formvalues', this.formValues);
    params.delete('type');

    const filtersParams = useFormValues
      ? new URLSearchParams(this.formValues)
      : new URLSearchParams(window.location.search);
    console.log('filterparams', filtersParams.toString());

    [...filtersParams.entries()].forEach(([key, val]) => {
      params.append(key, val);
    });

    // Remove choicesJS search box
    params.delete('search_terms');
    params.delete('Q');
    params.delete('timestamp');

    const data = await fetch(`${clearEndpoint}?${params.toString()}`).then((response) => {
      return response.text();
    });

    historyReplace({ search: params, hash: 'result' });
    this.updateContent(data, replace, innerList);
  }

  /**
   * updateContent
   * @this {Listing & ListingInterface}
   * @param {string} data
   * @param {boolean} replace
   * @param {boolean} innerList
   */
  updateContent(data, replace = false, innerList = false) {
    if (replace) {
      this.$refs.list.innerHTML = data;
    } else if (innerList) {
      this.$refs.innerlist.insertAdjacentHTML('beforeend', data);
    } else {
      this.$refs.list.insertAdjacentHTML('beforeend', data);
    }

    nextFrame(() => {
      this.$root.$update();
      if (innerList === false) {
        this.$refs.counter.innerHTML = this.$refs.count.innerHTML;
      }
    });
  }

  /**
   * History JS API popState event
   * Replace the TabContent with previous state
   * @this {Listing & ListingInterface}
   * @param {event} event The original event instance.
   */
  onPopstate(event) {
    if (event.state) {
      this.fetchData(this.$options.endpoint, true, false);
    }
  }

  /**
   * onResetClick
   * @this {Listing & ListingInterface}
   */
  onResetClick() {
    this.$refs.form.reset();

    if (this.$children.Select) {
      this.$children.Select.forEach((element) => {
        element.resetValue();
      });
    }

    if (this.$children.Autocomplete) {
      this.$children.Autocomplete.forEach((element) => {
        element.resetValue();
      });
    }
    this.fetchData(this.$options.endpoint, true, true);
  }

  /**
   * onCloseClick
   * @this {Listing & ListingInterface}
   */
  onCloseClick() {
    this.close();
  }

  /**
   * onOpenClick
   * @this {Listing & ListingInterface}
   */
  onOpenClick() {
    this.open();
  }

  /**
   * onLoadfromfilterClick
   * @param {object} event
   * @param {number} index
   */
  onLoadfromfilterClick(event, index) {
    const el = this.$refs.loadfromfilter[index];
    this.$children.Select[0].setValue(el.dataset.target);
  }

  /**
   * open Panel
   */
  async onOpenPanelClick() {
    if (this.$options.panelEndpoint && this.$children.Panel[0]) {
      // Get filters template once
      if (this.panelTemplate === '') {
        const template = await fetch(this.$options.panelEndpoint).then((response) => {
          return response.text();
        });

        this.panelTemplate = template;
        this.$children.Panel[0].$refs.content.innerHTML = template;
      }

      // open popin
      this.$children.Panel[0].open();
      nextFrame(() => {
        this.$root.$update();
      });
    }
  }

  /**
   * open fn
   */
  open() {
    this.$el.classList.add('is-filter-open');
  }

  /**
   * close fn
   */
  close() {
    this.$el.classList.remove('is-filter-open');
  }
}
